if (!customElements.get('product-add-ons')) {
  customElements.define(
    'product-add-ons',
    class ProductAddOns extends HTMLElement {
      constructor() {
        super();
        this.handleComponentClick = this.handleComponentClick.bind(this);
        this.handleComponentChange = this.handleComponentChange.bind(this);
        this.handleDrawerKeydown = this.handleDrawerKeydown.bind(this);
        this.handleQuantityChange = this.handleQuantityChange.bind(this);
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.productInfo = this.closest('product-info');
        this.productForm = document.getElementById(this.dataset.productFormId);
        this.dynamicCheckout = this.productForm ? this.productForm.querySelector('.shopify-payment-button') : null;
        this.liveRegion = this.querySelector('[data-addon-live-region]');
        this.drawerShell = this.querySelector('[data-addon-drawer-shell]');
        this.drawer = this.querySelector('[data-addon-drawer]');
        if (this.drawerShell) {
          document.body.appendChild(this.drawerShell);
          this.drawerShell.addEventListener('click', this.handleComponentClick);
        }
        this.mainVariantPrice = this.toInteger(this.dataset.mainVariantPrice);
        this.mainVariantCompareAtPrice = this.toInteger(this.dataset.mainVariantCompareAtPrice);

        this.addEventListener('click', this.handleComponentClick);
        this.addEventListener('change', this.handleComponentChange);
        this.bindQuantityInput();
        this.bindButtonPrice();
        this.subscribeToVariantChanges();
        this.syncAllCards();
        this.updateTotals(false);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleComponentClick);
        this.removeEventListener('change', this.handleComponentChange);
        this.unbindQuantityInput();
        if (this.drawerCloseTimer) {
          window.clearTimeout(this.drawerCloseTimer);
          this.drawerCloseTimer = null;
        }
        document.removeEventListener('keydown', this.handleDrawerKeydown);
        document.body.classList.remove('merwave-addons-drawer-open');
        if (this.drawerShell) {
          this.drawerShell.removeEventListener('click', this.handleComponentClick);
          this.drawerShell.remove();
        }
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.dynamicCheckout) this.dynamicCheckout.hidden = false;
        this.initialized = false;
      }

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || event.data.sectionId !== this.dataset.sectionId) return;
          if (event.data.variant) {
            this.mainVariantPrice = this.toInteger(event.data.variant.price);
            this.mainVariantCompareAtPrice = this.toInteger(
              event.data.variant.compare_at_price || event.data.variant.price
            );
          }
          this.bindQuantityInput();
          this.bindButtonPrice();
          this.updateTotals(false);
        });
      }

      bindButtonPrice() {
        this.productForm = document.getElementById(this.dataset.productFormId) || this.productForm;
        // The theme renders buy-buttons twice: once in the product column and
        // once inside the whole-page sticky bar. Both totals must track together.
        this.buttonPrices = Array.from(document.querySelectorAll('[data-product-add-ons-button-price]'));
      }

      bindQuantityInput() {
        const nextQuantityInput = this.productInfo
          ? this.productInfo.querySelector('.quantity__input[name="quantity"]')
          : null;

        if (this.quantityInput === nextQuantityInput) return;
        this.unbindQuantityInput();
        this.quantityInput = nextQuantityInput;
        if (this.quantityInput) this.quantityInput.addEventListener('change', this.handleQuantityChange);
      }

      unbindQuantityInput() {
        if (this.quantityInput) this.quantityInput.removeEventListener('change', this.handleQuantityChange);
        this.quantityInput = null;
      }

      handleQuantityChange() {
        this.updateTotals(true);
      }

      handleComponentClick(event) {
        const openButton = event.target.closest('[data-addon-drawer-open]');
        if (openButton) {
          this.openDrawer(openButton.dataset.addonDrawerOpen, openButton);
          return;
        }

        if (event.target.closest('[data-addon-drawer-close]')) {
          this.closeDrawer(true);
          return;
        }

        const card = event.target.closest('[data-addon-card]');
        if (!card || event.target.closest('a, button, input, select, label')) return;

        const checkbox = card.querySelector('[data-addon-checkbox]');
        if (checkbox && !checkbox.disabled) checkbox.click();
      }

      handleComponentChange(event) {
        if (event.target.matches('[data-addon-checkbox]')) {
          this.syncCardSelection(event.target.closest('[data-addon-card]'));
          this.updateTotals(true);
        }
      }

      syncAllCards() {
        this.querySelectorAll('[data-addon-card]').forEach((card) => {
          this.syncCardData(card);
          this.syncCardSelection(card);
        });
      }

      syncCardData(card) {
        if (!card) return;

        const checkbox = card.querySelector('[data-addon-checkbox]');
        if (!checkbox) return;

        const price = this.toInteger(checkbox.dataset.price);
        const compareAtPrice = this.toInteger(checkbox.dataset.compareAtPrice);
        const available = !checkbox.disabled;

        checkbox.dataset.price = String(price);
        if (!available) checkbox.checked = false;
        card.classList.toggle('is-unavailable', !available);

        const priceElement = card.querySelector('[data-addon-price]');
        const comparePriceElement = card.querySelector('[data-addon-compare-price]');
        if (priceElement) priceElement.textContent = this.formatMoney(price);
        if (comparePriceElement) {
          comparePriceElement.textContent = this.formatMoney(compareAtPrice);
          comparePriceElement.hidden = comparePriceElement.dataset.showCompare !== 'true' || compareAtPrice <= price;
        }

        const badge = card.querySelector('[data-addon-badge]');
        if (badge) {
          const showBadge = badge.dataset.showBadge === 'true';
          const discount = compareAtPrice > price ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice) : 0;
          badge.hidden = !showBadge || discount <= 0;
          badge.textContent = badge.dataset.badgeOverride || `${discount}% off`;
        }

        const cardIndex = card.dataset.addonIndex;
        const drawerPanel = this.drawerShell
          ? this.drawerShell.querySelector(`[data-addon-drawer-panel][data-addon-index="${cardIndex}"]`)
          : null;
        if (drawerPanel) {
          const drawerPrice = drawerPanel.querySelector('[data-addon-drawer-price]');
          if (drawerPrice) drawerPrice.textContent = this.formatMoney(price);
        }

        this.syncCardSelection(card);
      }

      syncCardSelection(card) {
        if (!card) return;
        const checkbox = card.querySelector('[data-addon-checkbox]');
        card.classList.toggle('is-selected', Boolean(checkbox && checkbox.checked));
      }

      getSelectedItems() {
        return Array.from(this.querySelectorAll('[data-addon-checkbox]:checked:not(:disabled)'))
          .slice(0, this.getMaxAddOns())
          .map((checkbox) => ({
            id: this.toInteger(checkbox.dataset.variantId || checkbox.value),
            quantity: 1,
          }))
          .filter((item) => item.id > 0);
      }

      updateTotals(announce) {
        const selectedCheckboxes = Array.from(
          this.querySelectorAll('[data-addon-checkbox]:checked:not(:disabled)')
        ).slice(0, this.getMaxAddOns());
        const mainQuantity = this.quantityInput ? Math.max(1, this.toInteger(this.quantityInput.value)) : 1;
        const addOnTotal = selectedCheckboxes.reduce(
          (total, checkbox) => total + this.toInteger(checkbox.dataset.price),
          0
        );
        const total = this.mainVariantPrice * mainQuantity + addOnTotal;

        this.renderButtonPrice(total);

        if (this.dynamicCheckout && this.dataset.hideDynamicCheckout === 'true') {
          this.dynamicCheckout.hidden = selectedCheckboxes.length > 0;
        }

        if (announce && this.liveRegion) {
          const count = selectedCheckboxes.length;
          const selectionMessage = count === 1 ? '1 add-on selected' : `${count} add-ons selected`;
          this.liveRegion.textContent = `${selectionMessage}. Total ${this.formatMoney(total)} ${this.currency}.`;
        }
      }

      renderButtonPrice(total) {
        if (!this.buttonPrices || !this.buttonPrices.length) this.bindButtonPrice();
        if (!this.buttonPrices || !this.buttonPrices.length) return;
        const currencyCode = this.dataset.showCurrencyCode === 'true' ? ` ${this.currency}` : '';
        const formatted = `${this.formatMoney(total)}${currencyCode}`;
        this.buttonPrices.forEach((element) => {
          element.textContent = formatted;
        });
      }

      openDrawer(panelId, opener) {
        if (!this.drawerShell || !this.drawer) return;
        const panel = this.drawerShell.querySelector(`#${this.escapeSelector(panelId)}`);
        if (!panel) return;

        if (this.drawerCloseTimer) window.clearTimeout(this.drawerCloseTimer);
        this.activeDrawerOpener = opener;
        this.drawerShell.querySelectorAll('[data-addon-drawer-panel]').forEach((candidate) => {
          candidate.hidden = candidate !== panel;
        });

        const drawerTitle = panel.querySelector('h2');
        if (drawerTitle && drawerTitle.id) {
          this.drawer.setAttribute('aria-labelledby', drawerTitle.id);
          this.drawer.removeAttribute('aria-label');
        }
        this.drawerShell.hidden = false;
        document.body.classList.add('merwave-addons-drawer-open');
        document.addEventListener('keydown', this.handleDrawerKeydown);

        requestAnimationFrame(() => {
          this.drawerShell.classList.add('is-open');
          const closeButton = this.drawer.querySelector('[data-addon-drawer-close]');
          if (closeButton) closeButton.focus();
        });
      }

      closeDrawer(restoreFocus) {
        if (!this.drawerShell || this.drawerShell.hidden) return;
        this.drawerShell.classList.remove('is-open');
        document.body.classList.remove('merwave-addons-drawer-open');
        document.removeEventListener('keydown', this.handleDrawerKeydown);

        this.drawerCloseTimer = window.setTimeout(() => {
          if (!this.drawerShell) return;
          this.drawerShell.hidden = true;
          this.drawerShell.querySelectorAll('[data-addon-drawer-panel]').forEach((panel) => {
            panel.hidden = true;
          });
          this.drawerCloseTimer = null;
        }, this.prefersReducedMotion() ? 0 : 220);

        if (restoreFocus && this.activeDrawerOpener) this.activeDrawerOpener.focus();
      }

      handleDrawerKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDrawer(true);
          return;
        }

        if (event.key !== 'Tab' || !this.drawer) return;
        const focusable = Array.from(
          this.drawer.querySelectorAll(
            'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => !element.closest('[hidden]'));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      formatMoney(cents) {
        const amount = this.toInteger(cents) / 100;
        const hasFraction = this.toInteger(cents) % 100 !== 0;

        try {
          return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: hasFraction ? 2 : 0,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          return `${this.currency} ${amount.toFixed(hasFraction ? 2 : 0)}`;
        }
      }

      toInteger(value) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      getMaxAddOns() {
        const configuredLimit = this.toInteger(this.dataset && this.dataset.maxAddOns);
        return Math.min(5, Math.max(1, configuredLimit || 5));
      }

      escapeSelector(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
        return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      }

      prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      get currency() {
        return this.dataset.currency || 'GBP';
      }
    }
  );
}
